import React from 'react';
import ExpandableSection from '@amzn/awsui-components-console/expandable-section';
import Badge from '@amzn/awsui-components-console/badge';
import Spinner from '@amzn/awsui-components-console/spinner';
import Table from '@amzn/awsui-components-console/table';
import Button from '@amzn/awsui-components-console/button';
import { apiEndPoint, origin } from 'src/config';
import $ from 'jquery';
import { getCognitoAuthSession } from 'src/authentication/cognito';
import ShowSnowStatus from 'src/components/findEntity/ShowSNOWStatus';
import moment from 'moment';
import { NOT_APPLICABLE } from 'src/constants/CommonConstants';

const dateFormat = 'Do MMM YYYY';

interface SubTaskRecord {
    Service: string;
    TaskSubTaskStatus: string;
    CreationTime: string;
    NotificationId?: string | number;
    LastStatusUpdateTime: string;
    sla?: number;
}

interface ProcessedSubTaskItem extends SubTaskRecord {
    expectedCompletionDate?: string;
    diff?: number;
}

interface Task {
    taskExecutionId: string;
    taskType: string;
    taskId: string;
    taskName: string;
    status: string;
}

interface SIMTrackerContainerProps {
    task: Task;
    workflowName: string;
    status: string;
}

interface SIMTrackerContainerState {
    loading: boolean;
    content: { subTasks: SubTaskRecord[] } | null;
    status: string;
    expanded: boolean;
}

function hasNotificationId(id: string | number | undefined): boolean {
	return id !== undefined && id !== null && id.toString().trim() !== '';
}

function getMostRecentRecord(records: SubTaskRecord[]): SubTaskRecord {
	return records.reduce((prev, curr) =>
		moment(curr.LastStatusUpdateTime).isAfter(moment(prev.LastStatusUpdateTime)) ? curr : prev
	);
}

function deduplicateSubTasksByNotificationId(subTaskRecords: SubTaskRecord[], taskName: string): SubTaskRecord[] {
	if (subTaskRecords.length === 0) return [];

	const allSameNonEmptyId = subTaskRecords.every((s) => hasNotificationId(s.NotificationId))
		&& new Set(subTaskRecords.map((s) => String(s.NotificationId))).size === 1;
	const allNoId = subTaskRecords.every((s) => !hasNotificationId(s.NotificationId));

	if (allSameNonEmptyId || allNoId) {
		return [{ ...getMostRecentRecord(subTaskRecords), Service: taskName }];
	}

	const grouped = new Map<string | number, SubTaskRecord[]>();
	const noIdRecords: SubTaskRecord[] = [];

	subTaskRecords.forEach((item) => {
		if (!hasNotificationId(item.NotificationId)) {
			noIdRecords.push(item);
		} else {
			const key = item.NotificationId!;
			const existing = grouped.get(key) || [];
			existing.push(item);
			grouped.set(key, existing);
		}
	});

	const deduplicated: SubTaskRecord[] = [];

	grouped.forEach((records) => {
		const latest = getMostRecentRecord(records);
		deduplicated.push(records.length > 1 ? { ...latest, Service: taskName } : latest);
	});

	if (noIdRecords.length > 1) {
		deduplicated.push({ ...getMostRecentRecord(noIdRecords), Service: taskName });
	} else if (noIdRecords.length === 1) {
		deduplicated.push(noIdRecords[0]);
	}

	return deduplicated;
}

function getItemsFromSubTasks(subTaskRecords: SubTaskRecord[], taskName: string): ProcessedSubTaskItem[] {
	const deduplicated = deduplicateSubTasksByNotificationId(subTaskRecords, taskName);

	return deduplicated.map((item) => {
		const base: ProcessedSubTaskItem = {
			Service: item.Service,
			TaskSubTaskStatus: item.TaskSubTaskStatus,
			CreationTime: moment(item.CreationTime).format(dateFormat),
			NotificationId: item.NotificationId,
			LastStatusUpdateTime: moment(item.LastStatusUpdateTime).format(dateFormat),
		};

		if (item.sla && item.sla > 0) {
			const expectedCompletionDate = moment(item.CreationTime).add(item.sla, 'days');
			base.sla = item.sla;
			base.expectedCompletionDate = expectedCompletionDate.format(dateFormat);
			base.diff = item.TaskSubTaskStatus === 'RESOLVED'
				? moment(item.LastStatusUpdateTime).diff(expectedCompletionDate, 'days')
				: moment().diff(expectedCompletionDate, 'days');
		}

		return base;
	});
}

function getSLA(item: ProcessedSubTaskItem): JSX.Element | null {
	if (!item.sla || item.diff === undefined)
		return null;

	if (item.TaskSubTaskStatus === 'RESOLVED') {
		if (Number(item.diff) > 0) {
			return <Badge color={'red'}> Missed by {Math.abs(item.diff)} days</Badge>;
		} else {
			return <Badge color={'green'}> Completed in {Math.abs(item.diff)} days</Badge>;
		}
	} else {
		if (Number(item.diff) > 0) {
			return <Badge color={'red'}> OverDue by {Math.abs(item.diff)} days</Badge>;
		} else {
			const toBeDue = Math.abs(item.diff);

			if (toBeDue <= 3) {
				return <Badge color={'grey'}> Due in {toBeDue} days</Badge>;
			}

			return <Badge color={'green'}> Due in {toBeDue} days</Badge>;
		}
	}
}

function getDataTable(subTaskRecords: SubTaskRecord[], taskName: string) {
	const columnDefinitions = [
		{
			id: 'Service',
			header: 'Service',
			cell: (item: ProcessedSubTaskItem) => item.Service,
		},
		{
			id: 'CreationTime',
			header: 'Created On',
			cell: (item: ProcessedSubTaskItem) => item.CreationTime,
		},
		{
			id: 'expectedCompletionDate',
			header: 'Expected Completion Date',
			cell: (item: ProcessedSubTaskItem) => item.expectedCompletionDate || NOT_APPLICABLE,
		},
		{
			id: 'LastStatusUpdateTime',
			header: 'Last Updated On',
			cell: (item: ProcessedSubTaskItem) => item.LastStatusUpdateTime,
		},
		{
			id: 'TaskSubTaskStatus',
			header: 'Status',
			cell: (item: ProcessedSubTaskItem) => item.TaskSubTaskStatus,
		},
		{
			id: 'OverDue',
			header: 'SLA',
			cell: (item: ProcessedSubTaskItem) => item.sla || NOT_APPLICABLE,
		},
		{
			id: 'diff',
			header: 'SLA Tracker',
			cell: (item: ProcessedSubTaskItem) => getSLA(item) || NOT_APPLICABLE,
		},
		{
			id: 'NotificationId',
			header: 'SIM / TT Link',
			cell: (item: ProcessedSubTaskItem) => item.NotificationId !== undefined ?
				<Button 
					iconName="external" 
					target={'_blank'} 
					href={item.NotificationId.toString().length > 15 ? 
						`https://issues.amazon.com/issues/${item.NotificationId}` : 
						`https://tt.amazon.com/${item.NotificationId}`
					} 
					iconAlign="right"
					variant="inline-link"
				>
                    Follow Up
				</Button> : <div>Not Created!</div>,
		},
	];
    
	const items = getItemsFromSubTasks(subTaskRecords, taskName);

	return <Table
		columnDefinitions={columnDefinitions}
		items={items}
		variant="embedded"
		wrapLines
		contentDensity="compact"
	/>;
}

export default class SIMTrackerContainer extends React.Component<SIMTrackerContainerProps, SIMTrackerContainerState> {
	constructor(props: SIMTrackerContainerProps) {
		super(props);
		this.state = {
			loading: true,
			content: null,
			status: props.status,
			expanded: true,
		};
		this.getSubTaskRecord = this.getSubTaskRecord.bind(this);
		this.handleExpandChange = this.handleExpandChange.bind(this);
	}

	componentDidMount(): void {
		this.getSubTaskRecord();
	}

	handleExpandChange({ detail }: { detail: { expanded: boolean } }): void {
		this.setState({ expanded: detail.expanded });
		if (detail.expanded && this.state.content === null) {
			this.getSubTaskRecord();
		}
	}

	async getSubTaskRecord(): Promise<void> {
		// Load it only once
		if (this.state.content === null) {
			console.log('Loading now');
			const url = `${apiEndPoint}task?executionId=${this.props.task.taskExecutionId}&workflowName=${this.props.workflowName}&taskType=${this.props.task.taskType}&taskId=${this.props.task.taskId}`;

			console.log(`Fetching the Sub Task Data from : ${url}`);
			const authorization = (await getCognitoAuthSession()).getIdToken().getJwtToken();
		
			$.ajax({
				url,
				dataType: 'json',
				Authorization: authorization,
				headers: {
					'Access-Control-Allow-Origin': origin,
					'Access-Control-Allow-Methods': 'GET,OPTIONS',
					'Access-Control-Allow-Headers': 'Access-Control-Allow-Headers,Access-Control-Allow-Credentials,Access-Control-Allow-Origin,Access-Control-Allow-Methods,Content-Type,X-Amz-Date,Authorization,X-Api-Key,X-Amz-Security-Token',
					'Access-Control-Allow-Credentials': 'true',
					Authorization: authorization,
				},
				type: 'GET',
				hrFields: {
					withCredentials: true,
					Authorization: authorization,
				},
				crossDomain: true,
				success: (response: { body: string }) => {
					const data = JSON.parse(response.body);

					console.log(`Got the sub task data: ${response.body}`);
					this.setState({
						loading: false,
						content: data,
					});
				},
				error: (jqXHR: unknown) => {
					console.log(`ERROR in Getting Sub Task Data AJAX request: ${JSON.stringify(jqXHR)}`);
					this.setState({
						loading: false,
					});
				},
			});
		}
	}

	render(): JSX.Element {
		return (
			<ExpandableSection
				expanded={this.state.expanded}
				onChange={this.handleExpandChange}
				headerText={this.props.task.taskName}
				headerActions={<ShowSnowStatus status={this.props.task.status} />}
				variant="container"
			>
				{this.state.loading ? (
					<Spinner size="normal" />
				) : (
					this.state.content && getDataTable(this.state.content.subTasks, this.props.task.taskName)
				)}
			</ExpandableSection>
		);
	}
}